import { useState, useMemo, useEffect } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import { useNavigate, useParams } from 'react-router'
import { Box, Button, Menu, MenuItem, TextField, InputAdornment, Typography, Divider } from '@mui/material'
import { ExpandMore, Search, SwapHoriz } from '@mui/icons-material'

import { queries, mutations } from '../../graphql'
import { usePermissions, useAuth, getImpersonatorToken } from '../../hooks'
import palette from '../../palette.json'

/**
 * FundSwitcher component for users with RBAC permissions to switch between funds
 * Shows accessible funds based on user's role assignments (e.g., financial_manager)
 *
 * Only visible to users who have fund-management related permissions
 */
export function FundSwitcher() {
  const navigate = useNavigate()
  const { fundId: currentFundId } = useParams()
  const [anchorEl, setAnchorEl] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [{ isImpersonating }, { impersonate, switchBack }] = useAuth()

  // State for funds fetched using impersonator's token
  const [impersonatorFunds, setImpersonatorFunds] = useState(null)
  const [impersonatorFundsLoading, setImpersonatorFundsLoading] = useState(false)

  // Check if user has financial manager or similar permissions
  const { hasAnyPermission, loading: permissionsLoading } = usePermissions()
  const hasFinancialAccess = hasAnyPermission([
    'fund:view',
    'fund:edit',
    'fund:manage_users',
    'manage:access',
    'system:admin',
  ])

  // Fetch accessible funds based on RBAC (only when not impersonating)
  const { data, loading: fundsLoading } = useQuery(queries.funds.myAccessibleFunds, {
    skip: !hasFinancialAccess || isImpersonating,
  })

  // Get current user's own funds to determine which funds belong to others (only when not impersonating)
  const { data: myFundsData } = useQuery(queries.funds.myFunds, {
    skip: !hasFinancialAccess || isImpersonating,
  })

  // When impersonating, fetch funds using the original (impersonator's) token
  useEffect(() => {
    if (isImpersonating) {
      const impersonatorToken = getImpersonatorToken()
      if (impersonatorToken) {
        setImpersonatorFundsLoading(true)
        const graphqlUrl = `${process.env.REACT_APP_API_HOST || ''}${process.env.REACT_APP_GRAPHQL_PATH || '/graphql'}`
        fetch(graphqlUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${impersonatorToken}`,
          },
          body: JSON.stringify({
            query: `
              query {
                me {
                  id
                  funds { id }
                  accessibleFunds {
                    id
                    name
                    number
                    userName
                  }
                }
              }
            `,
          }),
        })
          .then((res) => res.json())
          .then((result) => {
            if (result.data?.me) {
              setImpersonatorFunds({
                accessibleFunds: result.data.me.accessibleFunds || [],
                myFundIds: (result.data.me.funds || []).map((f) => f.id),
              })
            }
            setImpersonatorFundsLoading(false)
          })
          .catch((err) => {
            console.error('Failed to fetch impersonator funds:', err)
            setImpersonatorFundsLoading(false)
          })
      }
    } else {
      // Clear impersonator funds when not impersonating
      setImpersonatorFunds(null)
    }
  }, [isImpersonating])

  // Use impersonator's funds when impersonating, otherwise use current user's funds
  const myFundIds = isImpersonating
    ? impersonatorFunds?.myFundIds || []
    : (myFundsData?.me?.funds || []).map((f) => f.id)

  const [loginAsFundOwner, { loading: impersonating }] = useMutation(mutations.auth.loginAsFundOwner)

  const accessibleFunds = isImpersonating ? impersonatorFunds?.accessibleFunds || [] : data?.me?.accessibleFunds || []

  // Filter funds based on search term
  const filteredFunds = useMemo(() => {
    if (!searchTerm.trim()) {
      return accessibleFunds
    }
    const term = searchTerm.toLowerCase()
    return accessibleFunds.filter(
      (fund) =>
        fund.name.toLowerCase().includes(term) ||
        fund.number.toLowerCase().includes(term) ||
        (fund.userName && fund.userName.toLowerCase().includes(term))
    )
  }, [accessibleFunds, searchTerm])

  // Get current fund name
  const currentFund = accessibleFunds.find((f) => f.id === parseInt(currentFundId, 10))

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
    setSearchTerm('')
  }

  // Check if a fund belongs to another user (not the financial manager's own fund)
  const isOtherUsersFund = (fundId) => !myFundIds.includes(fundId)

  // Call loginAsFundOwner using a specific token (for when we need to use the impersonator's token)
  const loginAsFundOwnerWithToken = async (fundId, token) => {
    const apiUrl = `${process.env.REACT_APP_API_HOST || ''}/auth/loginAsFundOwner/${fundId}`
    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    })
    if (!response.ok) {
      throw new Error('Failed to login as fund owner')
    }
    return response.json()
  }

  const handleFundSelect = async (fundId) => {
    // If selecting another user's fund, impersonate the fund owner
    if (isOtherUsersFund(fundId)) {
      try {
        let newToken
        if (isImpersonating) {
          // When already impersonating, use the original financial manager's token
          const impersonatorToken = getImpersonatorToken()
          const result = await loginAsFundOwnerWithToken(fundId, impersonatorToken)
          newToken = result.access_token // eslint-disable-line camelcase
        } else {
          // When not impersonating, use the normal mutation
          const {
            data: { loginAsFundOwner: loginResult },
          } = await loginAsFundOwner({ variables: { fundId } })
          newToken = loginResult.access_token // eslint-disable-line camelcase
        }
        await impersonate(newToken)
        handleClose()
        navigate('/')
      } catch (err) {
        console.error('Failed to impersonate fund owner:', err)
      }
    } else {
      // Selecting own fund - switch back if impersonating, then navigate
      if (isImpersonating) {
        await switchBack()
      }
      navigate(`/funds/${fundId}`)
      handleClose()
    }
  }

  // Determine loading state
  const isLoading = isImpersonating ? impersonatorFundsLoading : permissionsLoading || fundsLoading

  // Don't show if loading
  if (isLoading) {
    return null
  }

  // Don't show if not impersonating and user doesn't have financial access
  if (!isImpersonating && !hasFinancialAccess) {
    return null
  }

  // Don't show if no accessible funds (but show for financial managers even with 1 fund)
  if (accessibleFunds.length === 0) {
    return null
  }

  return (
    <Box sx={{ mx: 1 }}>
      <Button
        variant="outlined"
        onClick={handleClick}
        startIcon={<SwapHoriz />}
        endIcon={<ExpandMore />}
        size="small"
        sx={{
          color: palette.black,
          borderColor: palette.grey,
          '&:hover': {
            borderColor: palette.dark.blue,
            color: palette.dark.blue,
          },
          textTransform: 'none',
          fontSize: '0.8rem',
          maxWidth: '200px',
        }}
      >
        <Typography
          noWrap
          sx={{
            maxWidth: '120px',
            fontSize: '0.8rem',
          }}
        >
          {currentFund?.name || 'Switch Fund'}
        </Typography>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            maxHeight: 400,
            width: 300,
          },
        }}
      >
        {/* Search field */}
        <Box sx={{ px: 2, py: 1 }} onKeyDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search funds..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
            autoFocus
          />
        </Box>

        <Divider />

        {/* Fund list */}
        {filteredFunds.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="textSecondary">
              No funds found
            </Typography>
          </MenuItem>
        ) : (
          filteredFunds.map((fund) => (
            <MenuItem
              key={fund.id}
              onClick={() => handleFundSelect(fund.id)}
              disabled={impersonating}
              selected={fund.id === parseInt(currentFundId, 10)}
              sx={{
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-child': {
                  borderBottom: 'none',
                },
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                },
              }}
            >
              <Box>
                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                  {fund.userName}
                </Typography>
                <Typography variant="body2" fontWeight={fund.id === parseInt(currentFundId, 10) ? 'bold' : 'normal'}>
                  {fund.name}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  #{fund.number}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>
    </Box>
  )
}
